import { useState, useEffect, FC, useRef } from "react";
import "../CSS/CalendarStrip.css";
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isBefore,
  startOfDay,
} from "date-fns";

interface CalendarStripProps {
  selectedDate: Date;
  setSelectedDate: (date: string) => void;
}

const CalendarStrip: FC<CalendarStripProps> = ({
  selectedDate,
  setSelectedDate,
}) => {
  const [monthDays, setMonthDays] = useState<Date[]>([]);
  const selectedRef = useRef<HTMLDivElement | null>(null);
  const hasScrolledInitially = useRef<boolean>(false);

  useEffect(() => {
    // Generate dates from the start of the current month to the end of the next month
    const today = startOfDay(new Date());
    const start = startOfMonth(today);
    const end = endOfMonth(new Date(today.getFullYear(), today.getMonth() + 1));

    const days = eachDayOfInterval({ start, end });
    setMonthDays(days);
  }, []);

  useEffect(() => {
    if (!hasScrolledInitially.current && selectedRef.current) {
      const parent = selectedRef.current.parentElement;
      if (parent && monthDays.length) {
        selectedRef.current.scrollIntoView({
          behavior: "auto",
          inline: "start",
          block: "nearest",
        });
        parent.scrollLeft = Math.max(0, parent.scrollLeft - 24);
      }
      hasScrolledInitially.current = true;
    }
  }, [monthDays]);

  const handleSelect = (date: Date): void => {
    setSelectedDate(date.toISOString());
  };

  return (
    <div className="calendar-strip">
      {monthDays.map((date: Date) => {
        const today = startOfDay(new Date());
        const isSelected =
          format(date, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
        // Disable if the date is strictly before today
        const isDisabled = isBefore(date, today);

        return (
          <div
            key={date.toString()}
            className={`calendar-day ${isSelected ? "selected" : ""} ${isDisabled ? "disabled" : ""}`}
            onClick={() => !isDisabled && handleSelect(date)}
            ref={isSelected ? selectedRef : null}
            style={isDisabled ? { pointerEvents: "none", opacity: 0.5 } : {}}
          >
            <div className="day-label">{format(date, "EEE").toUpperCase()}</div>
            <div className="day-number">{format(date, "d")}</div>
            <div className="day-label">{format(date, "MMM")}</div>
          </div>
        );
      })}
    </div>
  );
};

export default CalendarStrip;
